// ==========================================================
// Result / Evaluation Modal Controller
// ==========================================================

import type { EvaluationResult } from '../engine/Evaluator';
import { sounds } from '../audio/SoundEffects';
import { i18n } from '../i18n/I18nService';
import { ScoreManager } from '../engine/ScoreManager';

export class ResultModalController {
  private modalEl: HTMLElement;
  private stampTitleEl: HTMLElement;
  private stampSubEl: HTMLElement;
  private stampJpEl: HTMLElement;
  private headlineEl: HTMLElement;
  private starsContainer: HTMLElement;
  private valAccuracyEl: HTMLElement;
  private barAccuracyEl: HTMLElement;
  private valFinishEl: HTMLElement;
  private barFinishEl: HTMLElement;
  private valSpeedEl: HTMLElement;
  private barSpeedEl: HTMLElement;
  private valColorEl: HTMLElement;
  private barColorEl: HTMLElement;
  private rowColorEl: HTMLElement;
  private valCoinsEl: HTMLElement;
  private totalScoreEl: HTMLElement;
  private newHighBadgeEl: HTMLElement;
  private currentResult?: EvaluationResult;
  private isColorLabMode: boolean = false;
  private currentEarnedCoins: number = 0;

  private onRetryCallback?: () => void;
  private onNextCallback?: () => void;
  private onExportCallback?: () => void;

  constructor() {
    this.modalEl = document.getElementById('modal-result')!;
    this.stampTitleEl = document.getElementById('stamp-title')!;
    this.stampSubEl = document.querySelector('.stamp-sub') as HTMLElement;
    this.stampJpEl = document.querySelector('.stamp-jp') as HTMLElement;
    this.headlineEl = document.getElementById('result-headline')!;
    this.starsContainer = document.getElementById('result-stars')!;
    this.valAccuracyEl = document.getElementById('res-val-accuracy')!;
    this.barAccuracyEl = document.getElementById('res-bar-accuracy')!;
    this.valFinishEl = document.getElementById('res-val-finish')!;
    this.barFinishEl = document.getElementById('res-bar-finish')!;
    this.valSpeedEl = document.getElementById('res-val-speed')!;
    this.barSpeedEl = document.getElementById('res-bar-speed')!;
    this.valColorEl = document.getElementById('res-val-color')!;
    this.barColorEl = document.getElementById('res-bar-color')!;
    this.rowColorEl = document.getElementById('res-row-color')!;
    this.valCoinsEl = document.getElementById('res-val-coins')!;
    this.totalScoreEl = document.getElementById('res-total-score')!;
    this.newHighBadgeEl = document.getElementById('res-new-high-badge')!;

    this.initEvents();
    this.updateLanguageUI();

    i18n.subscribe(() => {
      this.updateLanguageUI();
      if (this.currentResult) {
        this.renderResult(this.currentResult, this.currentEarnedCoins, this.isColorLabMode);
      }
    });
  }

  private initEvents() {
    document.getElementById('btn-retry-stage')?.addEventListener('click', () => {
      sounds.playClick();
      this.close();
      if (this.onRetryCallback) this.onRetryCallback();
    });

    document.getElementById('btn-next-stage')?.addEventListener('click', () => {
      sounds.playClick();
      this.close();
      if (this.onNextCallback) this.onNextCallback();
    });

    document.getElementById('btn-export-image')?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onExportCallback) this.onExportCallback();
    });
  }

  public setCallbacks(callbacks: {
    onRetry: () => void;
    onNext: () => void;
    onExport: () => void;
  }) {
    this.onRetryCallback = callbacks.onRetry;
    this.onNextCallback = callbacks.onNext;
    this.onExportCallback = callbacks.onExport;
  }

  public show(
    stageId: number,
    result: EvaluationResult,
    earnedCoins: number = 0,
    isColorLabMode: boolean = false
  ) {
    this.currentResult = result;
    this.currentEarnedCoins = earnedCoins;
    this.isColorLabMode = isColorLabMode;

    // Save record via ScoreManager
    const saveRes = ScoreManager.saveStageScore(
      stageId,
      result.totalScore,
      result.stars,
      result.meisterTitle
    );

    if (this.newHighBadgeEl) {
      if (saveRes.isNewHigh && result.isPass) {
        this.newHighBadgeEl.classList.remove('hidden');
      } else {
        this.newHighBadgeEl.classList.add('hidden');
      }
    }

    this.renderResult(result, earnedCoins, isColorLabMode);
    this.modalEl.classList.remove('hidden');

    // SFX
    if (result.isPass) {
      sounds.playFanfare(result.stars);
    } else {
      sounds.playClick();
    }
  }

  private renderResult(result: EvaluationResult, earnedCoins: number, isColorLabMode: boolean) {
    const t = i18n.dict.result;

    // Localized Meister Title
    let localizedTitle = result.meisterTitle;
    if (result.totalScore >= 9500) localizedTitle = t.stamps.meister;
    else if (result.totalScore >= 8500) localizedTitle = t.stamps.expert;
    else if (result.totalScore >= 7000) localizedTitle = t.stamps.artisan;
    else localizedTitle = t.stamps.apprentice;

    this.stampTitleEl.textContent = localizedTitle;
    this.headlineEl.textContent = result.isPass ? t.passHeadline : t.failHeadline;

    if (this.stampSubEl) this.stampSubEl.textContent = t.stampSub;
    if (this.stampJpEl) this.stampJpEl.textContent = t.stampJp;

    // Stars
    this.starsContainer.innerHTML = '';
    for (let i = 1; i <= 3; i++) {
      const star = document.createElement('span');
      star.className = `star ${i <= result.stars ? 'filled' : 'empty'}`;
      star.textContent = i <= result.stars ? '★' : '☆';
      this.starsContainer.appendChild(star);
    }

    // Values & Progress Bars
    this.valAccuracyEl.textContent = `${result.accuracyScore}%`;
    this.valFinishEl.textContent = `${result.finishScore}%`;
    this.valSpeedEl.textContent = `+${result.speedBonus} pt`;
    this.totalScoreEl.textContent = result.totalScore.toLocaleString();

    if (this.valCoinsEl) {
      this.valCoinsEl.textContent = `+${earnedCoins.toLocaleString()} 🪙`;
    }

    // Reset Bars for animation
    this.barAccuracyEl.style.width = '0%';
    this.barFinishEl.style.width = '0%';
    this.barSpeedEl.style.width = '0%';
    this.barColorEl.style.width = '0%';

    if (isColorLabMode) {
      this.rowColorEl.style.display = 'flex';
      this.valColorEl.textContent = `${result.colorScore}%`;
      setTimeout(() => {
        this.barColorEl.style.width = `${result.colorScore}%`;
      }, 400);
    } else {
      this.rowColorEl.style.display = 'none';
    }

    // Animate Progress Bars sequentially
    setTimeout(() => {
      this.barAccuracyEl.style.width = `${result.accuracyScore}%`;
    }, 150);

    setTimeout(() => {
      this.barFinishEl.style.width = `${result.finishScore}%`;
    }, 300);

    setTimeout(() => {
      this.barSpeedEl.style.width = `${Math.min(100, (result.speedBonus / 400) * 100)}%`;
    }, 450);
  }

  public close() {
    this.modalEl.classList.add('hidden');
  }

  public updateLanguageUI() {
    const t = i18n.dict.result;

    const scoreLabels = document.querySelectorAll('.scores-grid .score-label');
    if (scoreLabels[0]) scoreLabels[0].textContent = t.accuracyLabel;
    if (scoreLabels[1]) scoreLabels[1].textContent = t.finishLabel;
    if (scoreLabels[2]) scoreLabels[2].textContent = t.speedLabel;
    if (scoreLabels[3]) scoreLabels[3].textContent = t.colorLabel;

    const coinsRowLabel = document.querySelector('#res-row-coins .score-label');
    if (coinsRowLabel) coinsRowLabel.textContent = `🪙 ${i18n.dict.app.earnedCoins}`;

    const totalLabel = document.querySelector('.total-label');
    if (totalLabel) totalLabel.textContent = t.totalLabel;

    const totalUnit = document.querySelector('.total-unit');
    if (totalUnit) totalUnit.textContent = t.unitPts;

    const exportBtnSpan = document.querySelector('#btn-export-image span');
    if (exportBtnSpan) exportBtnSpan.textContent = t.saveImage;

    const retryBtnSpan = document.querySelector('#btn-retry-stage span');
    if (retryBtnSpan) retryBtnSpan.textContent = t.retry;

    const nextBtnSpan = document.querySelector('#btn-next-stage span');
    if (nextBtnSpan) nextBtnSpan.textContent = t.nextStage;

    if (this.newHighBadgeEl) {
      this.newHighBadgeEl.textContent = i18n.dict.app.newHighScore;
    }
  }
}
